import { Static, Type } from '@sinclair/typebox';

// ── Enum ──────────────────────────────────────────────

export enum BookingCustomFieldType {
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  SELECT = 'SELECT',
  NUMBER = 'NUMBER',
  CHECKBOX = 'CHECKBOX',
  DATE = 'DATE',
}

export const BookingCustomFieldTypeSchema = Type.Enum(BookingCustomFieldType);

// ── Option (for SELECT type) ──────────────────────────

export const BookingCustomFieldOptionSchema = Type.Object({
  label: Type.String({ maxLength: 200 }),
  value: Type.String({ maxLength: 200 }),
});
export type BookingCustomFieldOptionObject = Static<typeof BookingCustomFieldOptionSchema>;

// ── Public response (returned in /public/booking/{slug}/prestations) ──

export const BookingCustomFieldPublicSchema = Type.Object({
  booking_custom_field_id: Type.Number(),
  label: Type.String(),
  type: BookingCustomFieldTypeSchema,
  required: Type.Boolean(),
  placeholder: Type.Optional(Type.String()),
  options: Type.Optional(Type.Array(BookingCustomFieldOptionSchema)),
  min_value: Type.Optional(Type.Number()),
  max_value: Type.Optional(Type.Number()),
  prestation_ids: Type.Array(Type.Number()),
  // Absent (backend antérieur à la portée par lieu) = aucune contrainte de lieu. Les tunnels filtrent
  // côté client : la portée DOIT voyager dans ce payload public (transformToSchema ne copie que les
  // clés déclarées ici).
  pro_place_ids: Type.Optional(Type.Array(Type.Number())),
  sort_order: Type.Number(),
});
export type BookingCustomFieldPublicObject = Static<typeof BookingCustomFieldPublicSchema>;

// La règle de visibilité d'un champ (prestations ET lieux, SOURCE UNIQUE tunnels classique et course +
// backend) vit dans ./booking_perimeter, partagée avec les instructions de réservation.

// ── Admin response (returned by the pro back-office endpoints) ────────

export const BookingCustomFieldAdminSchema = Type.Composite([
  BookingCustomFieldPublicSchema,
  Type.Object({
    is_archived: Type.Boolean(),
    created_at: Type.Optional(Type.String({ format: 'date-time' })),
    updated_at: Type.Optional(Type.String({ format: 'date-time' })),
  }),
]);
export type BookingCustomFieldAdminObject = Static<typeof BookingCustomFieldAdminSchema>;

// ── CRUD requests (pro back-office) ──────────────────

export const CreateBookingCustomFieldRequestSchema = Type.Object({
  label: Type.String({
    minLength: 1,
    maxLength: 255,
    title: 'BOOKING_CUSTOM_FIELD_LABEL',
    placeholder: 'BOOKING_CUSTOM_FIELD_LABEL_PLACEHOLDER',
  }),
  type: BookingCustomFieldTypeSchema,
  required: Type.Optional(Type.Boolean({ default: false })),
  placeholder: Type.Optional(
    Type.String({
      maxLength: 255,
      title: 'BOOKING_CUSTOM_FIELD_PLACEHOLDER',
    }),
  ),
  options: Type.Optional(Type.Array(BookingCustomFieldOptionSchema, { maxItems: 50 })),
  min_value: Type.Optional(Type.Number()),
  max_value: Type.Optional(Type.Number()),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  // Vide = tous les lieux ; sinon le champ n'est proposé que pour une réservation tenue dans l'un d'eux
  // (une course, sans lieu, ne le voit jamais).
  pro_place_ids: Type.Optional(Type.Array(Type.Integer())),
  sort_order: Type.Optional(Type.Number()),
});
export type CreateBookingCustomFieldRequestObject = Static<typeof CreateBookingCustomFieldRequestSchema>;

export const UpdateBookingCustomFieldRequestSchema = Type.Partial(CreateBookingCustomFieldRequestSchema);
export type UpdateBookingCustomFieldRequestObject = Static<typeof UpdateBookingCustomFieldRequestSchema>;

export const ReorderBookingCustomFieldsRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Number(), { minItems: 1 }),
});
export type ReorderBookingCustomFieldsRequestObject = Static<typeof ReorderBookingCustomFieldsRequestSchema>;

// ── Answer (sent in pre-reservation body) ─────────────

export const BookingCustomFieldAnswerSchema = Type.Object({
  booking_custom_field_id: Type.Number(),
  value: Type.String({ maxLength: 5000 }),
});
export type BookingCustomFieldAnswerObject = Static<typeof BookingCustomFieldAnswerSchema>;

// ── Answer (returned on appointment detail) ──────────
// Includes the field label/type so the UI can render the answer with context
// without doing an extra round-trip to the field config.

export const BookingCustomFieldAnswerDisplaySchema = Type.Object({
  booking_custom_field_id: Type.Number(),
  label: Type.String(),
  type: BookingCustomFieldTypeSchema,
  value: Type.String(),
});
export type BookingCustomFieldAnswerDisplayObject = Static<typeof BookingCustomFieldAnswerDisplaySchema>;
